import { Button, Card, CardContent, CardHeader, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, InputAdornment, LinearProgress, Link, Paper, Stack, Step, StepLabel, Stepper, TextField, Typography } from "@mui/material";
import { Info } from "lucide-react";
import { inject, observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { IAddProduct } from "../api/product/ProductsApi";
import CategorySelector from "../components/products/CategorySelector";
import LoadingComponent from "../components/util/LoadingComponent";
import RichTextEditor from "../components/util/textEditor/RichTextEditor";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

interface IProps {
  store?: RootStore;
}

const BULK_UPLOAD_MANDATORY_FIELDS = [
  "UPC",
  "Manufacturer Part Number (MPN)",
  "Product Title",
  "Category",
  "Standard Cost",
  "Master Carton",
  "Unit Weight"
];

const InsertProductPage: React.FC<IProps> = ({ store }) => {
  const { applicationId } = useManufacturer();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [mpn, setMpn] = useState("");
  const [map, setMap] = useState("");
  const [msrp, setMsrp] = useState("");
  const [bodyHTML, setBodyHTML] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [cost, setCost] = useState(0);
  const [unitWeight, setUnitWeight] = useState(0);
  const [masterCarton, setMasterCarton] = useState(1);
  const [skidQuantity, setSkidQuantity] = useState<number | undefined>();
  const [upc, setUpc] = useState("");
  const [redirect, setRedirect] = useState<string | undefined>();
  const [alreadyExistRedirect, setAlreadyExistRedirect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const manufacturer = store!.manufacturerStore.manufacturer;

  useEffect(() => {
    if (manufacturer === undefined) {
      store!.manufacturerStore.getManufacturerInfo(applicationId);
    }
  }, [applicationId, store, manufacturer]);

  const handleChange = useCallback((propName: string, propValue: string | number) => {
    const doubleRegEx = /^[0-9]+(\.[0-9]{0,2})?$|^$/;
    const value = String(propValue);

    console.log(`handleChange - propName: ${propName}, propValue: ${propValue}`);
    switch (propName) {
      case "UPC": setUpc(value); break;
      case "Title": setTitle(value); break;
      case "MPN": setMpn(value); break;
      case "Cost": if (doubleRegEx.test(value)) setCost(parseFloat(value) || 0); break;
      case "UnitWeight": if (doubleRegEx.test(value)) setUnitWeight(parseFloat(value) || 0); break;
      case "MasterCarton": setMasterCarton(parseInt(value, 10) || 0); break;
      case "BodyHTML": setBodyHTML(value); break;
      case "MAP": if (doubleRegEx.test(value)) setMap(value); break;
      case "MSRP": if (doubleRegEx.test(value)) setMsrp(value); break;
      case "SkidQuantity": setSkidQuantity(parseInt(value, 10) || undefined); break;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const insertProduct = useCallback(() => {
    setLoading(true);

    const parsedMap = map === "" ? undefined : parseFloat(map);
    const parsedMsrp = msrp === "" ? undefined : parseFloat(msrp);

    const body: IAddProduct = {
      UPC: upc || "",
      Category: selectedCategory || "",
      Title: title || "",
      MPN: mpn || "",
      StandardCost: cost,
      MasterCarton: masterCarton,
      UnitWeight: unitWeight,
      MAP: parsedMap,
      MSRP: parsedMsrp,
      BodyHTML: bodyHTML,
      SkidQuantity: skidQuantity
    };

    const productNumber = body.UPC.length > 0 ? body.UPC : body.MPN;
    const { productStore, snackStore } = store!;

    productStore.addNewProduct(applicationId, productNumber, body).then((product) => {
      if (product) {
        snackStore.showSuccess(`Product successfully added.`);
        setRedirect(product.randmarSKU);
      } else {
        snackStore.showError(`Could not add this product`);
        setLoading(false);
      }
    });
  }, [title, mpn, masterCarton, unitWeight, cost, upc, selectedCategory, map, msrp, bodyHTML, skidQuantity, applicationId, store]);

  const handleInsertProduct = useCallback(() => {
    setOpen(true);
  }, []);

  const handleUPCNextStep = useCallback(async () => {
    const { productStore } = store!;

    if (!upc) {
      setActiveStep(activeStep + 1);
      return;
    }

    setLoading(true);

    await productStore.getProduct(applicationId, upc);
    const { product } = productStore;

    if (product) {
      setAlreadyExistRedirect(true);
    } else {
      setActiveStep(activeStep + 1);
      setLoading(false);
    }
  }, [applicationId, store, upc, activeStep]);

  const handleIntegrationNextStep = useCallback(async () => {
    const { productStore, snackStore } = store!;
    setLoading(true);

    await productStore.getProduct(applicationId, mpn);

    if (productStore.product && productStore.product.isManufacturer) {
      snackStore.showError(`Product ${mpn} already exists`);
      setLoading(false);
      return;
    }

    setActiveStep(activeStep + 1);
    setLoading(false);
  }, [applicationId, store, mpn, activeStep]);

  const handleBack = useCallback(() => {
    setActiveStep(activeStep - 1);
  }, [activeStep]);

  const handleRedirect = useCallback(() => {
    const { product } = store!.productStore;
    navigate(Path.product(applicationId, product?.randmarSKU));
  }, [applicationId, store, navigate]);

  const steps = ["UPC", "Integration", "Distribution"];

  if (alreadyExistRedirect) {
    return (
      <Dialog open={true}>
        <DialogContent>
          <div>Product already exist in Randmar</div>
          <div>Navigate to product</div>
        </DialogContent>
        <DialogActions style={{ display: "flex", justifyContent: "space-between" }}>
          <Button onClick={handleRedirect}>Ok</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (redirect) return <Navigate to={Path.product(applicationId, redirect)} />;
  if (manufacturer === undefined) return <LoadingComponent />;

  return (
    <>
      <LinearProgress variant="determinate" value={(activeStep / 3) * 100} />
      <Container maxWidth="md">
        <Stepper activeStep={activeStep} alternativeLabel sx={{ my: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel color="inherit">{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper style={{ padding: "60px" }} >
          {activeStep === 0 && (
            <Stack direction="column" spacing={2}>
              <Typography variant="h1">UPC</Typography>
              <div className="flex items-start p-4 mb-4 text-sm text-blue-800 rounded-lg bg-blue-50 border border-blue-200" role="alert">
                <Info className="flex-shrink-0 inline w-5 h-5 mr-3 mt-1" />
                <span className="sr-only">Info</span>
                <div>
                  <h3 className="text-lg font-medium text-blue-900">What&apos;s a UPC code?</h3>A UPC code is usually 12-digits universal code.
                </div>
              </div>
              <TextField
                disabled={loading}
                label="UPC"
                value={upc}
                sx={{ maxWidth: 300 }}
                onChange={(e) => handleChange("UPC", e.target.value)}
              />
              <Stack direction="row" justifyContent={{ xs: "space-between", md: "left" }} spacing={2} paddingTop={{ xs: 1, md: 0 }}>
                <Button onClick={handleUPCNextStep} disabled={loading}>Skip</Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    loading ||
                    activeStep !== 0 ||
                    isNaN(+upc) ||
                    upc.length < 10 ||
                    upc.length > 14 ||
                    upc === ""
                  }
                  onClick={handleUPCNextStep}
                >Next Step</Button>
              </Stack>
            </Stack>
          )}
          {activeStep === 1 && (
            <div>
              <Stack direction="column" spacing={1}>
                <Typography variant="h1">Integration</Typography>
                <Typography>Product&apos;s description</Typography>
              </Stack>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={loading}
                    required
                    label="Manufacturer Part Number"
                    value={mpn}
                    onChange={(e) => handleChange("MPN", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CategorySelector
                    disabled={loading}
                    selectedCategory={selectedCategory}
                    defaultValue={selectedCategory}
                    onSelectedCategoryChange={(nc: string) => setSelectedCategory(nc)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={loading}
                    required
                    label="Product Title"
                    value={title}
                    onChange={(e) => handleChange("Title", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={loading}
                    label="MAP (Minimum Advertised Price)"
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    value={map}
                    onChange={(e) => handleChange("MAP", e.target.value)}
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ mb: 1 }}>Description</Typography>
                  <RichTextEditor
                    initialContent=""
                    setContent={(newValue: string) => handleChange("BodyHTML", newValue)}
                  />
                </Grid>
              </Grid>
              <Stack direction="row" justifyContent="space-between" mt={3}>
                <Button disabled={loading} variant="contained" color="primary" onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || activeStep !== 1 || title === "" || mpn === "" || selectedCategory === ""}
                  onClick={handleIntegrationNextStep}
                >Next Step</Button>
              </Stack>
            </div>
          )}
          {activeStep === 2 && (
            <div>
              <Stack direction="column" spacing={1}>
                <Typography variant="h1">Distribution</Typography>
                <Typography>Product&apos;s distribution information</Typography>
              </Stack>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={loading}
                    required
                    id="product-Cost"
                    label={`Standard Cost (${manufacturer?.currencyCode})`}
                    placeholder="Edit standard cost"
                    value={cost}
                    onChange={(e) => handleChange("Cost", e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    fullWidth
                    autoComplete="off"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={loading}
                    required
                    id="product-unitWeight"
                    label="Unit Weight (lbs)"
                    placeholder="Edit unit weight"
                    value={unitWeight}
                    onChange={(e) => handleChange("UnitWeight", e.target.value)}
                    fullWidth
                    autoComplete="off"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={loading}
                    required
                    id="product-masterCarton"
                    label="Master Carton"
                    placeholder="Edit master carton"
                    value={masterCarton}
                    onChange={(e) => handleChange("MasterCarton", e.target.value)}
                    fullWidth
                    autoComplete="off"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={loading}
                    id="product-skidQuantity"
                    label="Skid Quantity"
                    placeholder="Edit skid quantity"
                    value={skidQuantity}
                    onChange={(e) => handleChange("SkidQuantity", e.target.value)}
                    fullWidth
                    autoComplete="off"
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={loading}
                    id="product-msrp"
                    label={`MSRP (${manufacturer?.currencyCode})`}
                    placeholder="Edit MSRP"
                    value={msrp}
                    onChange={(e) => handleChange("MSRP", e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    fullWidth
                    autoComplete="off"
                    type="number"
                    helperText="Optional"
                  />
                </Grid>
              </Grid>
              <Stack direction="row" justifyContent="space-between" mt={2}>
                <Button disabled={loading} variant="contained" color="primary" onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loading || activeStep !== 2 || title === "" || mpn === "" || selectedCategory === "" || masterCarton === 0 || cost === 0 || !masterCarton || !cost}
                  onClick={handleInsertProduct}
                >Insert Product</Button>
              </Stack>
              <Dialog
                open={open}
                onClose={loading ? undefined : handleCancel}
                aria-labelledby="form-dialog-title"
              >
                <DialogTitle id="form-dialog-title">Insert {mpn}?</DialogTitle>
                <div hidden={loading}>
                  <DialogActions style={{ display: "flex", justifyContent: "space-between" }}>
                    <Button onClick={handleCancel} color="primary">Cancel</Button>
                    <Button variant="contained" onClick={insertProduct} color="primary">Confirm</Button>
                  </DialogActions>
                </div>
                <div hidden={!loading} style={{ textAlign: "center", padding: "20px" }}>
                  <Typography>Please wait...</Typography>
                  <LinearProgress />
                </div>
              </Dialog>
            </div>
          )}
        </Paper>
        <Card variant="outlined" sx={{ mt: 4, mb: 5 }}>
          <CardHeader
            title="Product Bulk Upload"
            subheader="Send us your spreadsheet and we'll insert products for you."
          />
          <CardContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Include these mandatory values in your .xlsx file so we can successfully insert every product:
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {BULK_UPLOAD_MANDATORY_FIELDS.map((field) => (
                <Chip key={field} label={field} color="primary" variant="outlined" size="small" />
              ))}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2">
              Email the completed spreadsheet as a .xlsx attachment to{" "}
              <Link href="mailto:echo@randmar.io">echo@randmar.io</Link>{" "}
              and our team will handle the bulk upload.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}

export default withRoot(inject("store")(observer(InsertProductPage)));
